import { Box, BoxProps } from "@chakra-ui/react";
import React, { useRef } from "react";

export type ResizeDirection =
  | "top"
  | "topRight"
  | "right"
  | "bottomRight"
  | "bottom"
  | "bottomLeft"
  | "left"
  | "topLeft";

export type RanaResizeCallback = (
  e: MouseEvent,
  direction: ResizeDirection,
  ref: HTMLDivElement,
  delta: { width: number; height: number }
) => void;

interface RanaResizableProps {
  size: { width: number; height: number };
  minWidth?: number;
  minHeight?: number;
  onResizeStart?: () => void;
  onResize?: RanaResizeCallback;
  onResizeStop?: RanaResizeCallback;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const HANDLE_SIZE = 4;
export const HANDLE_CLASS_NAME = "rana-resizable-handle";

type HandleConfig = Pick<
  BoxProps,
  "top" | "bottom" | "left" | "right" | "width" | "height" | "zIndex" | "cursor"
>;

const HANDLE_CONFIG: Record<ResizeDirection, HandleConfig> = {
  top: {
    cursor: "n-resize",
    top: -HANDLE_SIZE / 2,
    left: HANDLE_SIZE * 2,
    right: HANDLE_SIZE * 2,
    height: HANDLE_SIZE,
    zIndex: 1,
  },
  bottom: {
    cursor: "s-resize",
    bottom: -HANDLE_SIZE / 2,
    left: HANDLE_SIZE * 2,
    right: HANDLE_SIZE * 2,
    height: HANDLE_SIZE,
    zIndex: 1,
  },
  left: {
    cursor: "w-resize",
    left: -HANDLE_SIZE / 2,
    top: HANDLE_SIZE * 2,
    bottom: HANDLE_SIZE * 2,
    width: HANDLE_SIZE,
    zIndex: 1,
  },
  right: {
    cursor: "e-resize",
    right: -HANDLE_SIZE / 2,
    top: HANDLE_SIZE * 2,
    bottom: HANDLE_SIZE * 2,
    width: HANDLE_SIZE,
    zIndex: 1,
  },
  topLeft: {
    cursor: "nw-resize",
    top: -HANDLE_SIZE / 2,
    left: -HANDLE_SIZE / 2,
    width: HANDLE_SIZE * 2,
    height: HANDLE_SIZE * 2,
    zIndex: 2,
  },
  topRight: {
    cursor: "ne-resize",
    top: -HANDLE_SIZE / 2,
    right: -HANDLE_SIZE / 2,
    width: HANDLE_SIZE * 2,
    height: HANDLE_SIZE * 2,
    zIndex: 2,
  },
  bottomLeft: {
    cursor: "sw-resize",
    bottom: -HANDLE_SIZE / 2,
    left: -HANDLE_SIZE / 2,
    width: HANDLE_SIZE * 2,
    height: HANDLE_SIZE * 2,
    zIndex: 2,
  },
  bottomRight: {
    cursor: "se-resize",
    bottom: -HANDLE_SIZE / 2,
    right: -HANDLE_SIZE / 2,
    width: HANDLE_SIZE * 2,
    height: HANDLE_SIZE * 2,
    zIndex: 2,
  },
};

// Which mouse axes drive each direction: +1, -1, or 0
const DIRECTION_SIGNS: Record<ResizeDirection, { dx: number; dy: number }> = {
  right: { dx: 1, dy: 0 },
  left: { dx: -1, dy: 0 },
  bottom: { dx: 0, dy: 1 },
  top: { dx: 0, dy: -1 },
  bottomRight: { dx: 1, dy: 1 },
  bottomLeft: { dx: -1, dy: 1 },
  topRight: { dx: 1, dy: -1 },
  topLeft: { dx: -1, dy: -1 },
};

const DIRECTIONS: ResizeDirection[] = [
  "top",
  "topRight",
  "right",
  "bottomRight",
  "bottom",
  "bottomLeft",
  "left",
  "topLeft",
];

export const RanaResizable: React.FC<RanaResizableProps> = ({
  size,
  minWidth = 0,
  minHeight = 0,
  onResizeStart,
  onResize,
  onResizeStop,
  style,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const makeHandleMouseDown = (direction: ResizeDirection) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startW = size.width;
    const startH = size.height;
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const { dx: dxSign, dy: dySign } = DIRECTION_SIGNS[direction];

    onResizeStart?.();

    const computeDelta = (ev: MouseEvent): { width: number; height: number } => {
      const rawDW = (ev.clientX - startMouseX) * dxSign;
      const rawDH = (ev.clientY - startMouseY) * dySign;

      const newW = Math.max(startW + rawDW, minWidth);
      const newH = Math.max(startH + rawDH, minHeight);

      return { width: newW - startW, height: newH - startH };
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!wrapperRef.current) return;
      onResize?.(ev, direction, wrapperRef.current, computeDelta(ev));
    };

    const handleMouseUp = (ev: MouseEvent) => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (!wrapperRef.current) return;
      onResizeStop?.(ev, direction, wrapperRef.current, computeDelta(ev));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Box ref={wrapperRef} position="relative" width={size.width} height={size.height} sx={style}>
      {children}
      {DIRECTIONS.map((dir) => {
        const config = HANDLE_CONFIG[dir];
        return (
          <Box
            key={dir}
            className={`${HANDLE_CLASS_NAME} ${HANDLE_CLASS_NAME}-${dir}`}
            position="absolute"
            {...config}
            onMouseDown={makeHandleMouseDown(dir)}
          />
        );
      })}
    </Box>
  );
};
